import { useState, useEffect } from "react";
import axios from "axios";
import StockItemSearch from "../components/StockItemSearch";
import {
  Plus,
  ArrowRight,
  CheckCircle,
  Truck,
  Package,
  RefreshCw,
  ArrowLeftRight,
  Clock,
  XCircle,
  AlertTriangle,
  Eye,
  Send,
  Trash2,
  FileText,
  Lock,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import BookVehicleDialog, { BookVehicleButton } from "../components/BookVehicleDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import useAuthStore from "@/store/authStore";
import PermButton from "@/components/PermButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IBT = () => {
  const { user, token, hasBranchAccess } = useAuthStore();
  
  // Data
  const [transfers, setTransfers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [buyerSkus, setBuyerSkus] = useState([]);
  const [shortages, setShortages] = useState([]);
  const [branchStockItems, setBranchStockItems] = useState([]);
  const [stockItemsLoading, setStockItemsLoading] = useState(false);

  // Filters + pagination (replaces old summary-card-based quick filter)
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterSourceBranch, setFilterSourceBranch] = useState("ALL");
  const [filterDestBranch, setFilterDestBranch] = useState("ALL");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [showDispatchDialog, setShowDispatchDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [bookVehicleOpen, setBookVehicleOpen] = useState(false);

  // Delivery Challan (Zoho integration)
  const [showDCDialog, setShowDCDialog] = useState(false);
  const [dcTransfer, setDcTransfer] = useState(null);
  // When set, the DC dialog is in edit mode (PUT) — the dialog title reflects
  // it, the submit handler uses the update endpoint, and the lock-status badge
  // hides Edit once Zoho marks the DC non-draft.
  const [dcEditingId, setDcEditingId] = useState(null);
  const [dcSubmitting, setDcSubmitting] = useState(false);
  // Holds per-item missing-master errors when Zoho item creation isn't possible.
  // Shape: { items: [{internal_id, found_kind, missing_fields, reason}] } or null.
  const [dcItemBlockers, setDcItemBlockers] = useState(null);
  const [dcForm, setDcForm] = useState({
    customer_id: "",
    customer_name: "",
    challan_type: "others",
    date: new Date().toISOString().split("T")[0],
    reference_number: "",
    notes: "",
    terms_and_conditions: ""
  });
  const [dcCustomerQuery, setDcCustomerQuery] = useState("");
  const [dcCustomerResults, setDcCustomerResults] = useState([]);
  const [dcCustomerSearching, setDcCustomerSearching] = useState(false);
  const [dcBadgeOpenFor, setDcBadgeOpenFor] = useState(null); // transfer.id when popover open
  
  // Selected transfer
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  
  // Create form
  const [form, setForm] = useState({
    transfer_type: "RM",
    source_branch: "",
    destination_branch: "",
    notes: "",
    vehicle_number: "",
    driver_name: "",
    driver_contact: "",
    expected_arrival: ""
  });
  
  // Multi-item support - each row has item_id, quantity, and stock info
  const [itemRows, setItemRows] = useState([{ item_id: "", quantity: "", availableStock: null }]);
  
  // Legacy support - keep for backward compatibility
  const [itemsToTransfer, setItemsToTransfer] = useState([]);
  const [currentItem, setCurrentItem] = useState({ item_id: "", quantity: 0 });
  
  // Inventory check
  const [availableStock, setAvailableStock] = useState(null);
  const [checkingStock, setCheckingStock] = useState(false);
  
  // Receive form - supports both single and multi-item
  const [receiveForm, setReceiveForm] = useState({
    received_quantity: 0,
    items: [], // For multi-item transfers: [{item_id, received_quantity}]
    received_notes: "",
    damage_notes: ""
  });
  
  // Dispatch form
  const [dispatchForm, setDispatchForm] = useState({
    vehicle_number: "",
    driver_name: "",
    driver_contact: "",
    expected_arrival: ""
  });
  
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [submitting, setSubmitting] = useState(false);

  const getHeaders = () => ({ Authorization: `Bearer ${token}` });

  // Jul 2026 — Auto-DC-on-Ship: cache the branch → Zoho customer mapping so
  // the DC dialog can prefill from the destination branch and the "Create
  // DC now" button (shown when `dc_pending=true`) can act without a search.
  // Cached at page mount; not refetched on every dialog open because the
  // list is < 100 rows and admins rarely change it during a session.
  const [branchCustomerMap, setBranchCustomerMap] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/branches/zoho-customer-mappings`, {
          headers: getHeaders(),
        });
        const rows = Array.isArray(res.data?.mappings) ? res.data.mappings : [];
        const byName = {};
        for (const r of rows) {
          if (r?.name) byName[r.name] = r;
        }
        setBranchCustomerMap(byName);
      } catch (_e) {
        // Non-fatal — DC dialog falls back to manual search when mapping missing.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [ibtRes, branchRes, rmRes, skuRes, shortRes] = await Promise.all([
        axios.get(`${API}/ibt-transfers`, { headers: getHeaders() }),
        axios.get(`${API}/branches/names`, { headers: getHeaders() }),
        axios.get(`${API}/raw-materials`, { headers: getHeaders() }),
        axios.get(`${API}/sku-management/buyer-skus?page_size=100`, { headers: getHeaders() }),
        axios.get(`${API}/ibt-shortages`, { headers: getHeaders() }).catch(() => ({ data: [] }))
      ]);
      setTransfers(ibtRes.data);
      // Dedupe — /branches/names can return the same branch twice
      // (preview/prod data), which produced React duplicate-key warnings
      // on every SelectItem keyed by branch name.
      setBranches([...new Set(branchRes.data.branches || [])]);
      setRawMaterials(rmRes.data || []);
      // Handle both response formats: {buyer_skus: [...]} or {items: [...]}
      const skuData = skuRes.data;
      setBuyerSkus(skuData.buyer_skus || skuData.items || skuData || []);
      setShortages(shortRes.data || []);
    } catch (error) {
      toast.error("Failed to fetch IBT data");
    }
    setLoading(false);
  };

  // Fetch items with non-zero stock when source branch or transfer type changes
  const fetchBranchStockItems = async (transferType, sourceBranch) => {
    if (!sourceBranch) {
      setBranchStockItems([]);
      return;
    }
    setStockItemsLoading(true);
    try {
      const res = await axios.get(
        `${API}/ibt-transfers/branch-stock/${transferType}/${encodeURIComponent(sourceBranch)}`,
        { headers: getHeaders() }
      );
      setBranchStockItems(res.data.items || []);
    } catch (err) {
      console.error("Failed to fetch branch stock items:", err);
      setBranchStockItems([]);
    }
    setStockItemsLoading(false);
  };



  // Check inventory when source branch and item change
  const checkInventory = async (itemId) => {
    if (!form.source_branch || !itemId) {
      return null;
    }
    
    try {
      const res = await axios.get(
        `${API}/ibt-transfers/check-inventory/${form.transfer_type}/${itemId}/${encodeURIComponent(form.source_branch)}`,
        { headers: getHeaders() }
      );
      return res.data.available_stock;
    } catch (err) {
      return 0;
    }
  };
  
  // Check stock for a specific item row
  const checkRowStock = async (rowIndex, itemId) => {
    if (!form.source_branch || !itemId) {
      return;
    }
    const stock = await checkInventory(itemId);
    setItemRows(prev => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], availableStock: stock };
      return updated;
    });
  };
  
  // Update item row
  const updateItemRow = (index, field, value) => {
    setItemRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    
    // Check stock when item changes
    if (field === 'item_id' && value) {
      checkRowStock(index, value);
    }
  };
  
  // Add new item row
  const addItemRow = () => {
    setItemRows(prev => [...prev, { item_id: "", quantity: "", availableStock: null }]);
  };
  
  // Remove item row
  const removeItemRow = (index) => {
    if (itemRows.length === 1) {
      // Don't remove the last row, just clear it
      setItemRows([{ item_id: "", quantity: "", availableStock: null }]);
    } else {
      setItemRows(prev => prev.filter((_, i) => i !== index));
    }
  };
  
  // Get valid items from rows (items with both item_id and quantity)
  const getValidItems = () => {
    return itemRows.filter(row => row.item_id && row.quantity && parseFloat(row.quantity) > 0);
  };

  const handleCreateTransfer = async () => {
    if (!form.source_branch || !form.destination_branch) {
      toast.error("Please select source and destination branches");
      return;
    }

    // Typed-but-not-picked guard. Matches Dispatch.js fix: if a user paste/types
    // an item_id directly in the search input without clicking a dropdown
    // suggestion, item_id stays empty in React state even though the input
    // visually shows the code. Surface a precise row-level error instead of
    // the generic "add at least one item".
    const typedNotPicked = itemRows.findIndex(
      (r) => !r.item_id && (parseFloat(r.quantity) > 0 || r.notes)
    );
    if (typedNotPicked !== -1) {
      toast.error(
        `Row ${typedNotPicked + 1}: click an option from the search dropdown — typed text alone isn't a selection.`
      );
      return;
    }

    const validItems = getValidItems();
    if (validItems.length === 0) {
      toast.error("Please add at least one item with quantity");
      return;
    }
    
    if (form.source_branch === form.destination_branch) {
      toast.error("Source and destination cannot be the same");
      return;
    }
    
    // Validate stock for all items
    for (const item of validItems) {
      if (item.availableStock !== null && parseFloat(item.quantity) > item.availableStock) {
        toast.error(`Insufficient stock for ${item.item_id}. Only ${item.availableStock} available.`);
        return;
      }
    }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/ibt-transfers`, {
        transfer_type: form.transfer_type,
        source_branch: form.source_branch,
        destination_branch: form.destination_branch,
        items: validItems.map(i => ({ item_id: i.item_id, quantity: parseFloat(i.quantity) })),
        notes: form.notes,
        vehicle_number: form.vehicle_number || null,
        driver_name: form.driver_name || null,
        driver_contact: form.driver_contact || null,
        expected_arrival: form.expected_arrival || null
      }, { headers: getHeaders() });
      
      toast.success(`Transfer ${res.data.transfer?.transfer_code} created with ${validItems.length} item(s)`);
      setShowCreateDialog(false);
      resetForm();
      fetchAllData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_INVENTORY") {
        toast.error(detail.message || "Insufficient stock for one or more items");
      } else {
        toast.error(typeof detail === 'string' ? detail : "Failed to create transfer");
      }
    }
    setSubmitting(false);
  };

  const resetForm = () => {
    setForm({
      transfer_type: "RM",
      source_branch: "",
      destination_branch: "",
      notes: "",
      vehicle_number: "",
      driver_name: "",
      driver_contact: "",
      expected_arrival: ""
    });
    setItemRows([{ item_id: "", quantity: "", availableStock: null }]);
    setItemsToTransfer([]);
    setCurrentItem({ item_id: "", quantity: 0 });
    setAvailableStock(null);
  };

  // Note: Approval step removed - IBT can be dispatched directly after creation
  // The handleApprove function is kept for backward compatibility but is no longer used in UI

  const openDispatchDialog = (transfer) => {
    setSelectedTransfer(transfer);
    setDispatchForm({
      vehicle_number: transfer.vehicle_number || "",
      driver_name: transfer.driver_name || "",
      driver_contact: transfer.driver_contact || "",
      expected_arrival: transfer.expected_arrival || ""
    });
    setShowDispatchDialog(true);
  };

  // -------- Delivery Challan (Zoho) --------
  const openDCDialog = (transfer) => {
    setDcTransfer(transfer);
    setDcEditingId(null);
    setDcCustomerQuery("");
    setDcCustomerResults([]);
    setDcItemBlockers(null);
    // Prefill customer from destination branch's Zoho customer mapping
    // (Jul 2026). User can still override via the search box below by
    // clearing the pill first. When no mapping exists the fields stay
    // blank and the search-and-pick flow works exactly as before.
    const mapped = branchCustomerMap[transfer.destination_branch] || null;
    setDcForm({
      customer_id: mapped?.zoho_customer_id || "",
      customer_name: mapped?.zoho_customer_name || "",
      challan_type: "others",
      date: new Date().toISOString().split("T")[0],
      reference_number: transfer.transfer_code || "",
      notes: "",
      terms_and_conditions: ""
    });
    setShowDCDialog(true);
  };

  // Open the same DC dialog in EDIT mode — pre-fills from the existing DC
  // metadata on the IBT and switches submit handler to PUT. Only callable
  // when the DC is still in draft (badge gating handled in JSX).
  const openEditDCDialog = (transfer) => {
    setDcTransfer(transfer);
    setDcEditingId(transfer.id);
    setDcCustomerQuery(transfer.zoho_dc_customer_name || "");
    setDcCustomerResults([]);
    setDcItemBlockers(null);
    setDcForm({
      customer_id: transfer.zoho_dc_customer_id || "",
      customer_name: transfer.zoho_dc_customer_name || "",
      challan_type: transfer.zoho_dc_challan_type || "others",
      date: transfer.zoho_dc_date || new Date().toISOString().split("T")[0],
      reference_number: transfer.zoho_dc_reference_number || transfer.transfer_code || "",
      notes: transfer.zoho_dc_notes || "",
      terms_and_conditions: transfer.zoho_dc_terms_and_conditions || "",
    });
    setShowDCDialog(true);
  };

  // Debounced customer search — runs whenever query changes
  useEffect(() => {
    if (!showDCDialog) return;
    const q = dcCustomerQuery.trim();
    if (q.length < 2) {
      setDcCustomerResults([]);
      // Feb 2026 — always clear searching flag on sub-minimum query so
      // the visible min-char hint isn't hidden after backspacing from a
      // longer query while a request is in flight.
      setDcCustomerSearching(false);
      return;
    }
    let cancelled = false;
    setDcCustomerSearching(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API}/zoho/customers/search?q=${encodeURIComponent(q)}`,
          { headers: getHeaders() }
        );
        if (!cancelled) setDcCustomerResults(res.data || []);
      } catch (err) {
        if (!cancelled) {
          console.error("Customer search failed", err);
          setDcCustomerResults([]);
          toast.error("Failed to search customers");
        }
      } finally {
        if (!cancelled) setDcCustomerSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dcCustomerQuery, showDCDialog]);

  const submitDC = async () => {
    if (!dcTransfer) return;
    if (!dcForm.customer_id) {
      toast.error("Please select a customer");
      return;
    }
    setDcSubmitting(true);
    setDcItemBlockers(null);
    try {
      const payload = {
        customer_id: dcForm.customer_id,
        challan_type: dcForm.challan_type || "others",
        date: dcForm.date,
        reference_number: dcForm.reference_number || undefined,
        notes: dcForm.notes || undefined,
        terms_and_conditions: dcForm.terms_and_conditions || undefined
      };
      const res = dcEditingId
        ? await axios.put(
            `${API}/ibt-transfers/${dcEditingId}/delivery-challan`,
            payload,
            { headers: getHeaders() }
          )
        : await axios.post(
            `${API}/ibt-transfers/${dcTransfer.id}/delivery-challan`,
            payload,
            { headers: getHeaders() }
          );
      toast.success(
        dcEditingId
          ? `DC ${res.data.deliverychallan_number} updated and synced to Zoho.`
          : `Draft DC created in Zoho: ${res.data.deliverychallan_number}`
      );
      setShowDCDialog(false);
      setDcEditingId(null);
      fetchAllData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;

      // Structured "items missing master fields" error from backend (HTTP 422)
      if (detail && typeof detail === "object" && detail.code === "ITEM_MASTER_INCOMPLETE") {
        setDcItemBlockers(detail);
        toast.error(detail.message || "Some items need master data updates before the DC can be created.");
        return;
      }

      // Build a human-readable reason from whatever shape the error has so the
      // user is never left with a vague "Failed to create delivery challan".
      let reason = "Unknown error";
      if (typeof detail === "string") {
        reason = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        // FastAPI / Pydantic 422 validation array
        reason = detail.map((d) => `${(d.loc || []).join(".")}: ${d.msg}`).join("; ");
      } else if (detail && typeof detail === "object") {
        reason = detail.message || detail.error || JSON.stringify(detail);
      } else if (err.message) {
        reason = err.message;
      }

      // Common reason hints to make the error actionable
      const hint =
        status === 503
          ? " (Zoho integration not configured on the backend — check ZOHO env vars.)"
          : status === 502
          ? " (Zoho Books rejected the request — see the message above for details.)"
          : status === 401
          ? " (Auth/session issue — try refreshing the page.)"
          : "";

      toast.error(`Failed to create delivery challan: ${reason}${hint}`, { duration: 8000 });
    } finally {
      setDcSubmitting(false);
    }
  };

  const getDCBadgeColor = (zohoStatus) => {
    const map = {
      draft: "bg-zinc-200 text-zinc-700",
      open: "bg-blue-100 text-blue-700",
      delivered: "bg-green-100 text-green-700",
      returned: "bg-amber-100 text-amber-700",
      void: "bg-red-100 text-red-700",
      fulfilled: "bg-emerald-100 text-emerald-700",
    };
    return map[(zohoStatus || "").toLowerCase()] || "bg-zinc-200 text-zinc-700";
  };

  const handleDispatch = async () => {
    if (!selectedTransfer) return;
    
    setSubmitting(true);
    try {
      let url = `${API}/ibt-transfers/${selectedTransfer.id}/dispatch`;
      const params = new URLSearchParams();
      if (dispatchForm.vehicle_number) params.append("vehicle_number", dispatchForm.vehicle_number);
      if (dispatchForm.driver_name) params.append("driver_name", dispatchForm.driver_name);
      if (dispatchForm.driver_contact) params.append("driver_contact", dispatchForm.driver_contact);
      if (dispatchForm.expected_arrival) params.append("expected_arrival", dispatchForm.expected_arrival);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      await axios.put(url, {}, { headers: getHeaders() });
      toast.success("Transfer dispatched - stock deducted from source");
      setShowDispatchDialog(false);
      fetchAllData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_INVENTORY") {
        toast.error(`Cannot dispatch: Only ${detail.available} available`);
      } else {
        toast.error(typeof detail === 'string' ? detail : "Failed to dispatch");
      }
    }
    setSubmitting(false);
  };

  const openReceiveDialog = (transfer) => {
    setSelectedTransfer(transfer);
    
    // Check if multi-item transfer
    if (transfer.items?.length > 0) {
      // Initialize receive form with all items at their dispatched quantities
      setReceiveForm({
        items: transfer.items.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name || item.item_id,
          dispatched_quantity: item.dispatched_quantity || item.quantity,
          received_quantity: item.dispatched_quantity || item.quantity
        })),
        received_notes: "",
        damage_notes: ""
      });
    } else {
      // Legacy single-item format
      setReceiveForm({
        received_quantity: transfer.dispatched_quantity || transfer.quantity,
        items: [],
        received_notes: "",
        damage_notes: ""
      });
    }
    setShowReceiveDialog(true);
  };

  const handleReceive = async () => {
    if (!selectedTransfer) return;
    
    setSubmitting(true);
    try {
      // Build request body based on transfer type
      let requestBody = {
        received_notes: receiveForm.received_notes,
        damage_notes: receiveForm.damage_notes
      };
      
      if (receiveForm.items?.length > 0) {
        // Multi-item format
        requestBody.items = receiveForm.items.map(item => ({
          item_id: item.item_id,
          received_quantity: item.received_quantity
        }));
      } else {
        // Legacy single-item format
        requestBody.received_quantity = receiveForm.received_quantity;
      }
      
      const res = await axios.put(
        `${API}/ibt-transfers/${selectedTransfer.id}/receive`,
        requestBody,
        { headers: getHeaders() }
      );
      
      if (res.data.variance > 0) {
        toast.warning(`Received with variance: ${res.data.variance} units short. Shortage record created.`);
      } else {
        toast.success("Transfer received - stock added to destination");
      }
      setShowReceiveDialog(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to receive");
    }
    setSubmitting(false);
  };

  const handleCancel = async (transferId) => {
    if (!window.confirm("Are you sure you want to cancel this transfer?")) return;
    
    try {
      await axios.put(`${API}/ibt-transfers/${transferId}/cancel`, {}, { headers: getHeaders() });
      toast.success("Transfer cancelled");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel");
    }
  };

  const openDetailDialog = (transfer) => {
    setSelectedTransfer(transfer);
    setShowDetailDialog(true);
  };

  const getStatusBadge = (status) => {
    const colors = {
      INITIATED: "bg-gray-100 text-gray-700",
      READY_FOR_DISPATCH: "bg-blue-100 text-blue-700",
      APPROVED: "bg-blue-100 text-blue-700", // Legacy support
      IN_TRANSIT: "bg-purple-100 text-purple-700",
      DISPATCHED: "bg-purple-100 text-purple-700", // Zoho-driven equivalent of IN_TRANSIT
      RECEIVED: "bg-green-100 text-green-700",
      COMPLETED: "bg-green-100 text-green-700",
      CANCELLED: "bg-red-100 text-red-700",
      REJECTED: "bg-red-100 text-red-700",
    };
    const displayStatus = status === "READY_FOR_DISPATCH" ? "READY" : status?.replace("_", " ");
    return <Badge className={colors[status] || "bg-gray-200"}>{displayStatus}</Badge>;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'INITIATED': return <Clock className="w-4 h-4 text-gray-500" />;
      case 'READY_FOR_DISPATCH': return <CheckCircle className="w-4 h-4 text-blue-500" />;
      case 'APPROVED': return <CheckCircle className="w-4 h-4 text-blue-500" />; // Legacy
      case 'IN_TRANSIT': return <Truck className="w-4 h-4 text-purple-500" />;
      case 'DISPATCHED': return <Truck className="w-4 h-4 text-purple-500" />;
      case 'RECEIVED': return <Package className="w-4 h-4 text-green-500" />;
      case 'COMPLETED': return <Package className="w-4 h-4 text-green-500" />;
      case 'CANCELLED': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  const getItemName = (transfer) => {
    if (transfer.item_name) return transfer.item_name;
    if (transfer.transfer_type === "RM") {
      const rm = rawMaterials.find(r => r.rm_id === transfer.item_id);
      return rm?.description || transfer.item_id;
    } else {
      const sku = buyerSkus.find(s => s.buyer_sku_id === transfer.item_id);
      return sku?.name || transfer.item_id;
    }
  };

  const filteredTransfers = transfers.filter(t => {
    // Status filter
    if (filterStatus !== "ALL" && t.status !== filterStatus) return false;
    // Source branch filter
    if (filterSourceBranch !== "ALL" && t.source_branch !== filterSourceBranch) return false;
    // Destination branch filter
    if (filterDestBranch !== "ALL" && t.destination_branch !== filterDestBranch) return false;
    // Date range filter — compare against initiated_at's date part
    if (filterDateFrom || filterDateTo) {
      const initiated = t.initiated_at ? t.initiated_at.split("T")[0] : "";
      if (filterDateFrom && initiated < filterDateFrom) return false;
      if (filterDateTo && initiated > filterDateTo) return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredTransfers.length / PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);
  const pagedTransfers = filteredTransfers.slice(
    (effectivePage - 1) * PAGE_SIZE,
    effectivePage * PAGE_SIZE
  );

  // Reset to page 1 whenever any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterSourceBranch, filterDestBranch, filterDateFrom, filterDateTo]);

  const clearFilters = () => {
    setFilterStatus("ALL");
    setFilterSourceBranch("ALL");
    setFilterDestBranch("ALL");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  const pendingShortages = shortages.filter(s => s.status === "PENDING_INVESTIGATION").length;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="ibt-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ArrowLeftRight className="w-6 h-6" />
            Inter-Branch Transfers
          </h1>
          <p className="text-sm text-zinc-500">Transfer inventory between branches</p>
        </div>
        <PermButton entity="IBTTransfer" action="CREATE" onClick={() => setShowCreateDialog(true)} data-testid="create-ibt-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Transfer
        </PermButton>
      </div>

      {/* Shortage Alert */}
      {pendingShortages > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">{pendingShortages} Pending Shortage Investigation(s)</p>
            <p className="text-sm text-amber-600">Some transfers had variance between dispatched and received quantities.</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card data-testid="ibt-filters">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <Label className="text-xs font-semibold uppercase text-zinc-500">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Statuses</SelectItem>
                  <SelectItem value="READY_FOR_DISPATCH">Ready</SelectItem>
                  <SelectItem value="DISPATCHED">Dispatched</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-zinc-500">From Branch</Label>
              <Select value={filterSourceBranch} onValueChange={setFilterSourceBranch}>
                <SelectTrigger data-testid="filter-source-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Source Branches</SelectItem>
                  {branches.map(b => (
                    <SelectItem key={`src-${b}`} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-zinc-500">To Branch</Label>
              <Select value={filterDestBranch} onValueChange={setFilterDestBranch}>
                <SelectTrigger data-testid="filter-dest-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Destination Branches</SelectItem>
                  {branches.map(b => (
                    <SelectItem key={`dst-${b}`} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-zinc-500">Date From</Label>
              <Input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                data-testid="filter-date-from"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-zinc-500">Date To</Label>
              <Input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                data-testid="filter-date-to"
              />
            </div>
          </div>
          <div className="flex justify-between items-center mt-3 text-xs text-zinc-500">
            <span>Showing <strong>{pagedTransfers.length}</strong> of <strong>{filteredTransfers.length}</strong> transfer(s){filteredTransfers.length !== transfers.length && <> (filtered from {transfers.length})</>}</span>
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Transfers Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b">
                <tr>
                  <th className="text-left p-3">Transfer Code</th>
                  <th className="text-left p-3">From → To</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">DC</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedTransfers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-zinc-500">
                      No transfers found
                    </td>
                  </tr>
                ) : (
                  pagedTransfers.map(transfer => (
                    <tr key={transfer.id} className="border-b hover:bg-zinc-50">
                      <td className="p-3">
                        <span className="font-mono font-bold text-blue-600">{transfer.transfer_code}</span>
                        {transfer.variance > 0 && (
                          <Badge className="ml-2 bg-amber-100 text-amber-700">Shortage</Badge>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 text-xs">
                          <span>{transfer.source_branch}</span>
                          <ArrowRight className="w-3 h-3 text-zinc-400" />
                          <span>{transfer.destination_branch}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(transfer.status)}
                          {getStatusBadge(transfer.status)}
                        </div>
                      </td>
                      <td className="p-3">
                        {transfer.zoho_dc_id ? (
                          <div className="relative inline-block" data-testid={`dc-badge-${transfer.id}`}>
                            <button
                              type="button"
                              onClick={() => setDcBadgeOpenFor(dcBadgeOpenFor === transfer.id ? null : transfer.id)}
                              className={`text-xs px-2 py-1 rounded font-medium uppercase tracking-wide ${getDCBadgeColor(transfer.zoho_dc_status)}`}
                              title="Click to see DC number"
                            >
                              {transfer.zoho_dc_status || "draft"}
                            </button>
                            {dcBadgeOpenFor === transfer.id && (
                              <div className="absolute z-20 mt-1 left-0 bg-white border border-zinc-200 rounded shadow-lg px-3 py-2 text-xs whitespace-nowrap"
                                   data-testid={`dc-number-popover-${transfer.id}`}>
                                <div className="font-semibold text-zinc-700">DC Number</div>
                                <div className="font-mono text-blue-600 mt-0.5">{transfer.zoho_dc_number || transfer.zoho_dc_id}</div>
                                <button
                                  className="mt-1 text-zinc-400 hover:text-zinc-600 text-[10px] uppercase"
                                  onClick={() => setDcBadgeOpenFor(null)}
                                >Close</button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="p-3 text-xs text-zinc-500">
                        {new Date(transfer.initiated_at).toLocaleDateString()}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openDetailDialog(transfer)} title="View Details">
                            <Eye className="w-4 h-4" />
                          </Button>
                          
                          {/* Create DC button removed May 22 2026. DC creation
                              moved to the dedicated Delivery Challans tab under
                              Logistics. The DC status badge below still surfaces
                              the DC status here for IBTs that have one. */}

                          {/* Jul 2026 — "Create DC now" surfaced ONLY when the
                              IBT was shipped by a user who lacked Dispatch.CREATE
                              (auto-fire skipped, dc_pending stamped). Clicking
                              opens the same DC dialog with the destination
                              branch's Zoho customer already prefilled. */}
                          {!transfer.zoho_dc_id && transfer.dc_pending && (
                            <PermButton
                              entity="Dispatch" action="CREATE"
                              size="sm"
                              variant="outline"
                              className="text-orange-600 border-orange-300"
                              onClick={() => openDCDialog(transfer)}
                              data-testid={`create-dc-now-btn-${transfer.id}`}
                              title={
                                transfer.dc_pending_reason === "actor_lacks_dispatch_create"
                                  ? "Ship happened without Dispatch role — click to create the Zoho DC now"
                                  : `DC pending: ${transfer.dc_pending_reason || "auto-fire skipped"}`
                              }
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Create DC now
                            </PermButton>
                          )}

                          {/* Edit DC — only while Zoho still says draft. Once
                              the DC leaves draft, edits must happen in Zoho. */}
                          {transfer.zoho_dc_id && (transfer.zoho_dc_status || "draft").toLowerCase() === "draft" && (
                            <PermButton
                              entity="Dispatch" action="UPDATE"
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDCDialog(transfer)}
                              data-testid={`edit-dc-btn-${transfer.id}`}
                              title="Edit DC (syncs to Zoho)"
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Edit DC
                            </PermButton>
                          )}
                          
                          {(transfer.status === "IN_TRANSIT" || transfer.status === "DISPATCHED") && hasBranchAccess(transfer.destination_branch) && (
                            <PermButton entity="IBTTransfer" action="UPDATE" size="sm" variant="outline" className="text-green-600" onClick={() => openReceiveDialog(transfer)} data-testid={`receive-ibt-btn-${transfer.id}`}>
                              <Package className="w-4 h-4 mr-1" />
                              Receive
                            </PermButton>
                          )}
                          
                          {(transfer.status === "INITIATED" || transfer.status === "READY_FOR_DISPATCH" || transfer.status === "APPROVED") && (
                            <PermButton entity="IBTTransfer" action="UPDATE" size="sm" variant="ghost" className="text-red-500" onClick={() => handleCancel(transfer.id)} data-testid={`cancel-ibt-btn-${transfer.id}`}>
                              <XCircle className="w-4 h-4" />
                            </PermButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {filteredTransfers.length > 0 && (
            <div className="flex items-center justify-between p-3 border-t text-sm" data-testid="ibt-pagination">
              <div className="text-zinc-500">
                Page <strong>{effectivePage}</strong> of <strong>{totalPages}</strong> · {PAGE_SIZE} per page
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(1)}
                  disabled={effectivePage === 1}
                  data-testid="page-first-btn"
                >First</Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={effectivePage === 1}
                  data-testid="page-prev-btn"
                >Previous</Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={effectivePage === totalPages}
                  data-testid="page-next-btn"
                >Next</Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={effectivePage === totalPages}
                  data-testid="page-last-btn"
                >Last</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Transfer Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Create Inter-Branch Transfer</DialogTitle>
            <DialogDescription>Transfer RM or Finished Goods between branches</DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            <div>
              <Label>Transfer Type</Label>
              <Select value={form.transfer_type} onValueChange={(v) => { setForm({ ...form, transfer_type: v }); setItemRows([{ item_id: "", quantity: "", availableStock: null }]); fetchBranchStockItems(v, form.source_branch); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RM">Raw Material</SelectItem>
                  <SelectItem value="FG">Finished Goods (SKU)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Source Branch *</Label>
                <Select value={form.source_branch} onValueChange={(v) => { setForm({ ...form, source_branch: v }); setItemRows([{ item_id: "", quantity: "", availableStock: null }]); fetchBranchStockItems(form.transfer_type, v); }}>
                  <SelectTrigger data-testid="ibt-source-branch-trigger">
                    <SelectValue placeholder="From..." />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(b => {
                      const allowed = hasBranchAccess(b);
                      return (
                        <SelectItem
                          key={b}
                          value={b}
                          disabled={!allowed}
                          data-testid={`ibt-source-branch-option-${b}`}
                        >
                          <span className="flex items-center gap-2">
                            {!allowed && <Lock className="w-3 h-3 text-zinc-400" />}
                            {b}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Destination Branch *</Label>
                <Select value={form.destination_branch} onValueChange={(v) => setForm({ ...form, destination_branch: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="To..." />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.filter(b => b !== form.source_branch).map(b => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Separator />
            
            {/* Inline Item Rows */}
            <div className="space-y-3">
              {itemRows.map((row, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <Label className="text-xs text-zinc-500">Item {index + 1}</Label>
                    <StockItemSearch
                      branch={form.source_branch}
                      type={form.transfer_type}
                      value={row.item_id}
                      onSelect={(item) => {
                        const updated = [...itemRows];
                        updated[index] = { ...updated[index], item_id: item.item_id, availableStock: item.current_stock || null };
                        setItemRows(updated);
                      }}
                      disabled={!form.source_branch}
                      excludeIds={itemRows.filter((r, i) => i !== index && r.item_id).map(r => r.item_id)}
                      testId={`item-search-${index}`}
                    />
                    {row.availableStock !== null && row.item_id && (
                      <p className={`text-xs mt-1 ${row.availableStock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        Available: {row.availableStock}
                      </p>
                    )}
                  </div>
                  
                  <div className="w-24">
                    <Label className="text-xs text-zinc-500">Qty</Label>
                    <Input 
                      type="number" 
                      min="0"
                      placeholder="Qty"
                      value={row.quantity}
                      onChange={(e) => updateItemRow(index, 'quantity', e.target.value)}
                      className={row.availableStock !== null && parseFloat(row.quantity) > row.availableStock ? 'border-red-500' : ''}
                    />
                  </div>
                  
                  {/* Show delete button for all rows except first if it's the only one */}
                  {(itemRows.length > 1 || row.item_id) && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700 mt-5"
                      onClick={() => removeItemRow(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  
                  {/* Show Add button only on the last row */}
                  {index === itemRows.length - 1 && (
                    <Button 
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-5"
                      onClick={addItemRow}
                      disabled={!row.item_id || !row.quantity}
                    >
                      <Plus className="w-4 h-4" />
                      Add
                    </Button>
                  )}
                  
                  {/* Placeholder for alignment when not the last row */}
                  {index !== itemRows.length - 1 && (
                    <div className="w-16" />
                  )}
                </div>
              ))}
            </div>
            
            <Separator />
            
            <p className="text-sm font-medium text-zinc-600">Transit Details (Optional)</p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vehicle Number</Label>
                <Input 
                  placeholder="MH-12-AB-1234"
                  value={form.vehicle_number}
                  onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })}
                />
              </div>
              <div>
                <Label>Expected Arrival</Label>
                <Input 
                  type="date"
                  value={form.expected_arrival}
                  onChange={(e) => setForm({ ...form, expected_arrival: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Driver Name</Label>
                <Input 
                  placeholder="Driver name"
                  value={form.driver_name}
                  onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Driver Contact</Label>
                <Input 
                  placeholder="Phone number"
                  value={form.driver_contact}
                  onChange={(e) => setForm({ ...form, driver_contact: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea 
                placeholder="Additional notes..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0 pt-4 border-t">
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTransfer} 
              disabled={submitting || !form.source_branch || !form.destination_branch || getValidItems().length === 0}
            >
              {submitting ? "Creating..." : `Create Transfer (${getValidItems().length} item${getValidItems().length !== 1 ? 's' : ''})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispatch Dialog */}
      <Dialog open={showDispatchDialog} onOpenChange={setShowDispatchDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dispatch Transfer</DialogTitle>
            <DialogDescription>
              Dispatching {selectedTransfer?.transfer_code} - {selectedTransfer?.items?.length || 1} item(s)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg text-sm">
              <p><strong>From:</strong> {selectedTransfer?.source_branch}</p>
              <p><strong>To:</strong> {selectedTransfer?.destination_branch}</p>
              {selectedTransfer?.items?.length > 0 ? (
                <div className="mt-2">
                  <p className="font-medium">Items:</p>
                  <ul className="list-disc list-inside text-xs mt-1 max-h-32 overflow-y-auto">
                    {selectedTransfer.items.map((item, idx) => (
                      <li key={idx}>{item.item_id} - Qty: {item.quantity}{selectedTransfer.transfer_type === "FG" ? " pcs" : ""}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <>
                  <p><strong>Item:</strong> {selectedTransfer?.item_id}</p>
                  <p><strong>Quantity:</strong> {selectedTransfer?.quantity}{selectedTransfer?.transfer_type === "FG" ? " pcs" : ""}</p>
                </>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vehicle Number</Label>
                <Input 
                  placeholder="MH-12-AB-1234"
                  value={dispatchForm.vehicle_number}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, vehicle_number: e.target.value })}
                />
              </div>
              <div>
                <Label>Expected Arrival</Label>
                <Input 
                  type="date"
                  value={dispatchForm.expected_arrival}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, expected_arrival: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Driver Name</Label>
                <Input 
                  value={dispatchForm.driver_name}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, driver_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Driver Contact</Label>
                <Input 
                  value={dispatchForm.driver_contact}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, driver_contact: e.target.value })}
                />
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Warning:</strong> Dispatching will deduct {selectedTransfer?.quantity} units from {selectedTransfer?.source_branch} inventory.
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDispatchDialog(false)}>Cancel</Button>
            <Button onClick={handleDispatch} disabled={submitting} className="bg-purple-600 hover:bg-purple-700">
              {submitting ? "Dispatching..." : "Dispatch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Receive Transfer</DialogTitle>
            <DialogDescription>
              Receiving {selectedTransfer?.transfer_code} at {selectedTransfer?.destination_branch}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-green-50 p-3 rounded-lg text-sm">
              <p><strong>From:</strong> {selectedTransfer?.source_branch}</p>
              {selectedTransfer?.items?.length > 0 ? (
                <p><strong>Items:</strong> {selectedTransfer.items.length} item(s)</p>
              ) : (
                <>
                  <p><strong>Item:</strong> {selectedTransfer?.item_id}</p>
                  <p><strong>Dispatched Qty:</strong> {selectedTransfer?.dispatched_quantity || selectedTransfer?.quantity}</p>
                </>
              )}
            </div>
            
            {/* Multi-item receive */}
            {receiveForm.items?.length > 0 ? (
              <div className="space-y-3">
                <Label>Received Quantities *</Label>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {receiveForm.items.map((item, idx) => (
                    <div key={item.item_id} className="bg-zinc-50 p-3 rounded-lg" data-testid={`receive-item-row-${item.item_id}`}>
                      <p className="text-xs font-mono text-zinc-600">{item.item_id}</p>
                      <p className="text-sm font-medium truncate">{item.item_name}</p>
                      <p className="text-xs text-zinc-500 mb-2">Dispatched: {item.dispatched_quantity}</p>
                      <Input 
                        type="number"
                        min="0"
                        max={item.dispatched_quantity}
                        value={item.received_quantity}
                        onChange={(e) => {
                          const newItems = [...receiveForm.items];
                          newItems[idx].received_quantity = parseFloat(e.target.value) || 0;
                          setReceiveForm({ ...receiveForm, items: newItems });
                        }}
                      />
                      {item.received_quantity < item.dispatched_quantity && (
                        <p className="text-xs text-amber-600 mt-1">
                          Shortage: {item.dispatched_quantity - item.received_quantity} units
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <Label>Received Quantity *</Label>
                <Input 
                  type="number"
                  min="0"
                  max={selectedTransfer?.dispatched_quantity || selectedTransfer?.quantity}
                  value={receiveForm.received_quantity}
                  onChange={(e) => setReceiveForm({ ...receiveForm, received_quantity: parseFloat(e.target.value) || 0 })}
                />
                {receiveForm.received_quantity < (selectedTransfer?.dispatched_quantity || selectedTransfer?.quantity) && (
                  <p className="text-xs text-amber-600 mt-1">
                    Variance: {(selectedTransfer?.dispatched_quantity || selectedTransfer?.quantity) - receiveForm.received_quantity} units will be recorded as shortage
                  </p>
                )}
              </div>
            )}
            
            <div>
              <Label>Damage Notes (if any)</Label>
              <Textarea 
                placeholder="Describe any damage..."
                value={receiveForm.damage_notes}
                onChange={(e) => setReceiveForm({ ...receiveForm, damage_notes: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Receipt Notes</Label>
              <Textarea 
                placeholder="Additional notes..."
                value={receiveForm.received_notes}
                onChange={(e) => setReceiveForm({ ...receiveForm, received_notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiveDialog(false)}>Cancel</Button>
            <Button onClick={handleReceive} disabled={submitting} className="bg-green-600 hover:bg-green-700">
              {submitting ? "Receiving..." : "Confirm Receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BookVehicleDialog
        open={bookVehicleOpen}
        onClose={() => setBookVehicleOpen(false)}
        sourceType="IBT"
        sourceId={selectedTransfer?.id}
        sourceLabel={selectedTransfer ? `Transfer ${selectedTransfer.transfer_code}` : ""}
      />

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getStatusIcon(selectedTransfer?.status)}
              Transfer {selectedTransfer?.transfer_code}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTransfer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-zinc-500">Status</p>
                  <div>{getStatusBadge(selectedTransfer.status)}</div>
                </div>
                <div>
                  <p className="text-zinc-500">Type</p>
                  <div><Badge variant="outline">{selectedTransfer.transfer_type}</Badge></div>
                </div>
                <div>
                  <p className="text-zinc-500">Source Branch</p>
                  <p className="font-medium">{selectedTransfer.source_branch}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Destination Branch</p>
                  <p className="font-medium">{selectedTransfer.destination_branch}</p>
                </div>
              </div>
              
              {/* Items Section */}
              <div className="flex justify-end">
                <BookVehicleButton onClick={() => setBookVehicleOpen(true)} />
              </div>
              {selectedTransfer.items?.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-zinc-500 text-sm">Items ({selectedTransfer.items.length})</p>
                  <div className="bg-zinc-50 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    {selectedTransfer.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-white p-2 rounded border text-sm">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{item.item_id}</p>
                          <p className="text-xs text-zinc-500 truncate">{item.item_name}</p>
                        </div>
                        <div className="text-right ml-2">
                          <p className="font-mono">{item.dispatched_quantity || item.quantity}{selectedTransfer.transfer_type === "FG" ? " pcs" : ""}</p>
                          {item.received_quantity !== undefined && (
                            <p className="text-xs">
                              Rcvd: {item.received_quantity}{selectedTransfer.transfer_type === "FG" ? " pcs" : ""}
                              {item.variance > 0 && <span className="text-red-500 ml-1">(-{item.variance})</span>}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between text-sm font-medium">
                    <span>Total:</span>
                    <span>
                      {selectedTransfer.total_quantity || selectedTransfer.items.reduce((sum, i) => sum + (i.dispatched_quantity || i.quantity), 0)}
                      {selectedTransfer.total_received !== undefined && (
                        <span className="text-zinc-500 ml-2">
                          (Received: {selectedTransfer.total_received})
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="col-span-2">
                    <p className="text-zinc-500">Item</p>
                    <p className="font-medium">{selectedTransfer.item_id}</p>
                    <p className="text-xs text-zinc-500">{getItemName(selectedTransfer)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Quantity</p>
                    <p className="font-medium">{selectedTransfer.quantity}{selectedTransfer.transfer_type === "FG" ? " pcs" : ""}</p>
                  </div>
                  {selectedTransfer.received_quantity !== undefined && (
                    <div>
                      <p className="text-zinc-500">Received</p>
                      <p className="font-medium">
                        {selectedTransfer.received_quantity}
                        {selectedTransfer.variance > 0 && (
                          <span className="text-red-500 ml-1">(-{selectedTransfer.variance})</span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              <Separator />
              
              <div className="text-sm space-y-2">
                <p className="font-medium">Timeline</p>
                <div className="space-y-1 text-xs">
                  <p><span className="text-zinc-500">Initiated:</span> {new Date(selectedTransfer.initiated_at).toLocaleString()}</p>
                  {selectedTransfer.approved_at && <p><span className="text-zinc-500">Approved:</span> {new Date(selectedTransfer.approved_at).toLocaleString()}</p>}
                  {selectedTransfer.dispatched_at && <p><span className="text-zinc-500">Dispatched:</span> {new Date(selectedTransfer.dispatched_at).toLocaleString()}</p>}
                  {selectedTransfer.received_at && <p><span className="text-zinc-500">Received:</span> {new Date(selectedTransfer.received_at).toLocaleString()}</p>}
                </div>
              </div>
              
              {(selectedTransfer.vehicle_number || selectedTransfer.driver_name) && (
                <>
                  <Separator />
                  <div className="text-sm space-y-1">
                    <p className="font-medium">Transit Details</p>
                    {selectedTransfer.vehicle_number && <p><span className="text-zinc-500">Vehicle:</span> {selectedTransfer.vehicle_number}</p>}
                    {selectedTransfer.driver_name && <p><span className="text-zinc-500">Driver:</span> {selectedTransfer.driver_name}</p>}
                    {selectedTransfer.driver_contact && <p><span className="text-zinc-500">Contact:</span> {selectedTransfer.driver_contact}</p>}
                    {selectedTransfer.expected_arrival && <p><span className="text-zinc-500">Expected:</span> {selectedTransfer.expected_arrival}</p>}
                  </div>
                </>
              )}
              
              {selectedTransfer.notes && (
                <>
                  <Separator />
                  <div className="text-sm">
                    <p className="font-medium">Notes</p>
                    <p className="text-zinc-600">{selectedTransfer.notes}</p>
                  </div>
                </>
              )}
              
              {selectedTransfer.variance > 0 && (
                <>
                  <Separator />
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                    <p className="font-medium text-amber-800">Shortage Recorded</p>
                    <p className="text-amber-700">
                      {selectedTransfer.variance} units short. 
                      {selectedTransfer.damage_notes && ` Damage: ${selectedTransfer.damage_notes}`}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            {selectedTransfer
              && (selectedTransfer.status === "IN_TRANSIT" || selectedTransfer.status === "DISPATCHED")
              && hasBranchAccess(selectedTransfer.destination_branch) && (
              <PermButton
                entity="IBTTransfer"
                action="UPDATE"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => { setShowDetailDialog(false); openReceiveDialog(selectedTransfer); }}
                data-testid="detail-dialog-receive-btn"
              >
                <Package className="w-4 h-4 mr-1" />
                Receive at destination
              </PermButton>
            )}
            {/* Hint shown when user is the wrong branch user for this receive */}
            {selectedTransfer
              && (selectedTransfer.status === "IN_TRANSIT" || selectedTransfer.status === "DISPATCHED")
              && !hasBranchAccess(selectedTransfer.destination_branch) && (
              <div className="text-xs text-zinc-500 italic mr-auto self-center" data-testid="receive-branch-locked-hint">
                Only users with access to <strong>{selectedTransfer.destination_branch}</strong> can receive this transfer.
              </div>
            )}
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create / Edit Delivery Challan Dialog (Phase 2 bidir sync) */}
      <Dialog open={showDCDialog} onOpenChange={(o) => { setShowDCDialog(o); if (!o) setDcEditingId(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              {dcEditingId
                ? `Edit Delivery Challan — ${dcTransfer?.zoho_dc_number || ""}`
                : "Create Delivery Challan"}
            </DialogTitle>
            <DialogDescription>
              {dcEditingId
                ? "Edits push immediately to Zoho. Once Zoho marks the DC as Open, this becomes view-only."
                : "Creates a draft DC in Zoho Books. Once you mark it as Open in Zoho, the IBT will auto-advance to DISPATCHED."}
            </DialogDescription>
          </DialogHeader>

          {dcTransfer && (
            <div className="space-y-4">
              {/* Item-master gap warning — appears after a 422 from the backend */}
              {dcItemBlockers?.items?.length > 0 && (
                <div
                  className="border border-amber-300 bg-amber-50 rounded-md p-3 text-xs"
                  data-testid="dc-item-blockers"
                >
                  <div className="font-semibold text-amber-900 mb-2 flex items-center gap-1.5">
                    <span className="bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide">
                      Action needed
                    </span>
                    Items missing master data — DC cannot be created in Zoho yet
                  </div>
                  <ul className="space-y-1.5 text-amber-900/90">
                    {dcItemBlockers.items.map((it) => (
                      <li
                        key={it.internal_id}
                        className="flex items-start gap-2"
                        data-testid={`dc-blocker-${it.internal_id}`}
                      >
                        <span className="font-mono font-bold text-amber-900 min-w-[90px]">{it.internal_id}</span>
                        <span className="flex-1">
                          {it.missing_fields?.length > 0 ? (
                            <>
                              Missing in {it.found_kind} master:{" "}
                              <span className="font-mono text-amber-800">
                                {it.missing_fields.join(", ")}
                              </span>
                            </>
                          ) : (
                            it.reason
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2 text-[11px] text-amber-800">
                    Update the missing fields in the relevant master (RM Repository or Buyer SKU) and try again.
                  </div>
                </div>
              )}

              {/* Auto-populated read-only context from IBT */}
              <div className="grid grid-cols-2 gap-3 bg-zinc-50 p-3 rounded text-sm">
                <div>
                  <Label className="text-xs text-zinc-500">Transfer Code</Label>
                  <div className="font-mono font-bold text-blue-600">{dcTransfer.transfer_code}</div>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Source → Destination</Label>
                  <div>{dcTransfer.source_branch} → {dcTransfer.destination_branch}</div>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-zinc-500">Items</Label>
                  <div className="text-xs">
                    {dcTransfer.items?.length > 0
                      ? dcTransfer.items.map(i => `${i.item_id} × ${i.quantity}`).join(", ")
                      : `${dcTransfer.item_id} × ${dcTransfer.quantity}`}
                  </div>
                </div>
                {(dcTransfer.vehicle_number || dcTransfer.driver_name) && (
                  <div className="col-span-2 text-xs text-zinc-600">
                    {dcTransfer.vehicle_number && <>Vehicle: <span className="font-mono">{dcTransfer.vehicle_number}</span></>}
                    {dcTransfer.vehicle_number && dcTransfer.driver_name && " · "}
                    {dcTransfer.driver_name && <>Driver: {dcTransfer.driver_name}</>}
                  </div>
                )}
              </div>

              {/* Customer search */}
              <div>
                <Label>Customer <span className="text-red-500">*</span></Label>
                <Input
                  value={dcCustomerQuery}
                  onChange={(e) => {
                    setDcCustomerQuery(e.target.value);
                    setDcForm({ ...dcForm, customer_id: "", customer_name: "" });
                  }}
                  placeholder="Type at least 2 characters to search Zoho customers..."
                  data-testid="dc-customer-search"
                />
                {dcForm.customer_id && (
                  <div className="mt-1 text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Selected: <span className="font-medium">{dcForm.customer_name}</span>
                  </div>
                )}
                {/* Feb 2026 — defect C: visible min-char gate hint. */}
                {!dcForm.customer_id && dcCustomerQuery.length > 0 && dcCustomerQuery.length < 2 && (
                  <div className="text-xs text-zinc-500 italic mt-1" data-testid="dc-customer-search-hint">
                    Type at least 2 characters…
                  </div>
                )}
                {dcCustomerQuery.length >= 2 && !dcForm.customer_id && (
                  <div className="border border-zinc-200 rounded mt-1 max-h-48 overflow-y-auto bg-white">
                    {dcCustomerSearching && (
                      <div className="p-2 text-xs text-zinc-400 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Searching...
                      </div>
                    )}
                    {!dcCustomerSearching && dcCustomerResults.length === 0 && (
                      <div className="p-2 text-xs text-zinc-400">No matches</div>
                    )}
                    {dcCustomerResults.map(c => (
                      <button
                        key={c.contact_id}
                        type="button"
                        className="w-full text-left p-2 text-sm hover:bg-blue-50 border-b last:border-b-0"
                        data-testid={`dc-customer-option-${c.contact_id}`}
                        onClick={() => {
                          setDcForm({ ...dcForm, customer_id: c.contact_id, customer_name: c.contact_name });
                          setDcCustomerQuery(c.contact_name);
                          setDcCustomerResults([]);
                        }}
                      >
                        <div className="font-medium">{c.contact_name}</div>
                        {(c.company_name || c.gst_no) && (
                          <div className="text-xs text-zinc-500">
                            {c.company_name}{c.company_name && c.gst_no && " · "}{c.gst_no}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Challan Type <span className="text-red-500">*</span></Label>
                  <Select value={dcForm.challan_type} onValueChange={(v) => setDcForm({ ...dcForm, challan_type: v })}>
                    <SelectTrigger data-testid="dc-challan-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="others">Others (recommended default)</SelectItem>
                      <SelectItem value="job_work">Job Work</SelectItem>
                      <SelectItem value="goods_sent_on_approval">Goods Sent on Approval</SelectItem>
                      <SelectItem value="skip_outward_registration">Skip Outward Registration</SelectItem>
                      <SelectItem value="supply_of_liquid_gas">Supply of Liquid Gas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Challan Date <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={dcForm.date}
                    onChange={(e) => setDcForm({ ...dcForm, date: e.target.value })}
                    data-testid="dc-date"
                  />
                </div>
              </div>

              <div>
                <Label>Reference Number</Label>
                <Input
                  value={dcForm.reference_number}
                  onChange={(e) => setDcForm({ ...dcForm, reference_number: e.target.value })}
                  placeholder="Defaults to IBT transfer code"
                  data-testid="dc-reference"
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={dcForm.notes}
                  onChange={(e) => setDcForm({ ...dcForm, notes: e.target.value })}
                  placeholder="If left blank, IBT notes + vehicle/driver info will be used."
                  rows={2}
                  data-testid="dc-notes"
                />
              </div>

              <div>
                <Label>Terms & Conditions</Label>
                <Textarea
                  value={dcForm.terms_and_conditions}
                  onChange={(e) => setDcForm({ ...dcForm, terms_and_conditions: e.target.value })}
                  rows={2}
                  data-testid="dc-terms"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDCDialog(false)} disabled={dcSubmitting}>
              Cancel
            </Button>
            <Button onClick={submitDC} disabled={dcSubmitting || !dcForm.customer_id} data-testid="dc-submit-btn">
              {dcSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {dcEditingId ? "Save & Sync to Zoho" : "Create Draft DC in Zoho"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IBT;
